import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import Api from "services/api";
import { Select } from "components/common/Select";
import ErrorMessage from "components/common/ErrorMessage";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";

const InterpreterCancelActionModal = ({ isOpen, notification, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { handleSubmit, control, reset, watch } = useForm({
    defaultValues: { interpreter_id: null },
  });

  const selectedInterpreter = watch("interpreter_id");

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setError("");
      reset({ interpreter_id: null });
    }
  }, [isOpen, reset]);

  const invitedInterpreters = notification?.invited_interpreters || [];

  const interpreterOptions = useMemo(
    () => invitedInterpreters.map((item) => ({ value: item.id, label: item.name })),
    [invitedInterpreters]
  );

  const selectedInterpreterName = useMemo(() => {
    const interpreter = invitedInterpreters.find((item) => item.id === selectedInterpreter);
    return interpreter ? interpreter.name : "";
  }, [invitedInterpreters, selectedInterpreter]);

  const handleCancelAppointment = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await Api.cancelAppointmentAndNotifyClient(
        notification.appointment_id,
        notification.cancellation_reason
      );
      if (response?.status >= 200 && response?.status < 300) {
        onSuccess({ type: "cancel" });
        navigate(`/appointments/view/${notification.appointment_id}`);
      } else {
        setError(response?.data?.message || "Failed to cancel appointment.");
      }
    } catch (err) {
      setError(err?.message || "An error occurred while cancelling appointment.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignInterpreter = async (data) => {
    const interpreterId = data?.interpreter_id;
    setError("");
    if (!interpreterId) {
      setError("Please choose an interpreter.");
      return;
    }
    setLoading(true);
    try {
      const response = await Api.assignInterpreter(
        {
          appointment_id: notification.appointment_id,
          interpreter_id: interpreterId,
        },
        "assign-interpreter"
      );

      if (response?.status >= 200 && response?.status < 300) {
        onSuccess({ type: "assign", interpreterName: selectedInterpreterName });
        navigate(`/appointments/view/${notification.appointment_id}`);
      } else {
        setError(response?.data?.message || "Failed to assign interpreter.");
      }
    } catch (err) {
      setError(err?.message || "An error occurred while assigning interpreter.");
    } finally {
      setLoading(false);
    }
  };

  const AppointmentTimezone = notification.appointment_details?.AppointmentTimezone?.appointment_details?.timezone;
  const rawDatetime = notification?.datetime;

  const formattedDefaultTime = AppointmentTimezone?.name
      ? moment.tz(rawDatetime, AppointmentTimezone.name).tz("America/Denver").format("hh:mm A")
      : moment(rawDatetime).format("hh:mm A");

  const formattedDate = AppointmentTimezone?.name
      ? moment.tz(rawDatetime, AppointmentTimezone.name).format("MMM D, YYYY")
      : moment(rawDatetime).format("MMM D, YYYY");

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div>
          <div className="row-primary">
            <h2>Appointment #:</h2> <h2>{notification.appointment_number}</h2>
          </div>
          <div className="row-primary">
            <h2>Date:</h2> <h2>{formattedDate ? formattedDate :notification.appointment_details?.date || "N/A"}</h2>
          </div>
          <div className="row-primary">
            <h2>Start Time:</h2> <h2>{formattedDefaultTime ? formattedDefaultTime :notification.appointment_details?.start_time || "N/A"}</h2>
          </div>
          <div className="row-primary">
            <h2>Type:</h2> <h2>{notification.appointment_details?.type || "N/A"}</h2>
          </div>
          <div className="row-primary">
            <h2>Language:</h2> <h2>{notification.appointment_details?.language || "N/A"}</h2>
          </div>
          <div className="row-primary">
            <h2>Location:</h2> <h2>{notification.appointment_details?.location || "N/A"}</h2>
          </div>
          <div className="row-primary">
            <h2>Interpreter Name:</h2> <h2>{notification.cancelled_interpreter?.name || "N/A"}</h2>
          </div>
          <div className="row-primary">
            <h2>Cancellation reason:</h2> <h2>{notification.cancellation_reason || "No reason provided."}</h2>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="step_cards">
          <h2>How would you like to proceed?</h2>
          <div className="d-flex gap-2">
            <button className="main_button  tomatoRed" onClick={handleCancelAppointment} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Cancel Appointment & Notify Client"}
            </button>
            <button className="main_button  green" onClick={() => setStep(3)} disabled={loading}>
              Assign to Another Interpreter
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="mb-3 row-primary">
          <h2 htmlFor="interpreterSelect" className="form-label">
            Select an interpreter
          </h2>
          <Controller
            name="interpreter_id"
            control={control}
            rules={{ required: "Please select an Interpreter" }}
            render={({ field, fieldState }) => (
              <div style={{ width: '100%' }}>
                <Select
                  name="interpreter_id"
                  options={interpreterOptions}
                  loading={false}
                  value={field.value ? interpreterOptions.find((opt) => opt.value == field.value) ?? null : null}
                  onChange={(rawValue) => field.onChange(rawValue ?? null)}
                  placeholder="Choose interpreter"
                />
                {fieldState.error?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={fieldState.error.message} />
                  </div>
                )}
              </div>
            )}
          />
        </div>
        {invitedInterpreters.length === 0 && (
          <div className="text-muted">No invited interpreters are available for reassignment.</div>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className="modal-medium">
      <ModalHeader toggle={onClose} className="modal-title_u">Interpreter Cancellation Action - Step {step} of 3</ModalHeader>
      <ModalBody className="modal_form_body">
        {error && <div className="alert alert-danger">{error}</div>}
        {renderStepContent()}
      </ModalBody>
      <ModalFooter className="row_shift Step_change modal-footer">
        {step > 1 && (
          <button type='button' onClick={() => setStep(step - 1)} disabled={loading} className='main_button'>
            <img src="/images/arrow-right.png" alt />
          </button>
        )}
        {step === 1 && (
          <button type='button' onClick={() => setStep(2)} className='main_button'>
            <img src="/images/arrow-left.png" alt="" style={{ filter: 'grayscale(1) brightness(5)' }} />
          </button>
        )}
        {step === 3 && (
         
          <button class="main_button" onClick={handleSubmit(handleAssignInterpreter)} disabled={!selectedInterpreter || loading}>
            {loading ? <Spinner size="sm" /> : "Assign"}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default InterpreterCancelActionModal;
